import { useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import { Separator } from 'src/components/ui/separator';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  CircleNotchIcon,
  PlusIcon,
  TrashIcon,
  WarningIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { useToast } from 'src/context/ToastContext';
import useTestGlJournalImport from 'src/api/temp/famous/useTestGlJournalImport';
import type {
  GlJournalHeader,
  GlJournalLine,
} from 'src/api/temp/famous/useTestGlJournalImport';
import { T, Var, useGT } from 'gt-react';

interface JournalLineFormData {
  id: string;
  costCenterId: string;
  phaseId: string;
  departmentId: string;
  accountId: string;
  postingDate: string;
  description: string;
  hours: string;
  quantity: string;
  debit: string;
  credit: string;
}

const createEmptyLine = (): JournalLineFormData => ({
  id: crypto.randomUUID(),
  costCenterId: '',
  phaseId: '',
  departmentId: '',
  accountId: '',
  postingDate: new Date().toISOString().split('T')[0],
  description: '',
  hours: '',
  quantity: '',
  debit: '0',
  credit: '0',
});

export function GlJournalTestCard() {
  const gt = useGT();
  const { showToast } = useToast();

  // Header state
  const [header, setHeader] = useState<GlJournalHeader>({
    description: '',
    journalGroup: '',
    accessGroup: '',
    accrualFlag: 'N',
    cashFlag: 'N',
  });

  // Lines state
  const [lines, setLines] = useState<JournalLineFormData[]>([
    createEmptyLine(),
    createEmptyLine(),
  ]);

  // UI state
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    payload?: string;
    errorFileContent?: string;
    totalDebit?: string;
    totalCredit?: string;
  } | null>(null);

  const { mutate: testImport, isPending } = useTestGlJournalImport();

  const handleHeaderChange = (field: keyof GlJournalHeader, value: string) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (
    lineId: string,
    field: keyof JournalLineFormData,
    value: string,
  ) => {
    setLines((prev) =>
      prev.map((line) =>
        line.id === lineId ? { ...line, [field]: value } : line,
      ),
    );
  };

  const addLine = () => {
    setLines((prev) => [...prev, createEmptyLine()]);
  };

  const removeLine = (lineId: string) => {
    setLines((prev) => prev.filter((line) => line.id !== lineId));
  };

  const handleSubmit = () => {
    setResult(null);

    const formattedLines: GlJournalLine[] = lines.map((line) => ({
      costCenterId: line.costCenterId || undefined,
      phaseId: line.phaseId || undefined,
      departmentId: line.departmentId || undefined,
      accountId: line.accountId || undefined,
      postingDate: line.postingDate,
      description: line.description || undefined,
      hours: line.hours ? parseFloat(line.hours) : undefined,
      quantity: line.quantity ? parseFloat(line.quantity) : undefined,
      debit: parseFloat(line.debit) || 0,
      credit: parseFloat(line.credit) || 0,
    }));

    testImport(
      {
        header,
        lines: formattedLines,
        dryRun,
      },
      {
        onSuccess: (response) => {
          setResult({
            success: response.data.success,
            message: response.data.message,
            payload: response.data.payload,
            errorFileContent: response.data.errorFileContent,
            totalDebit: response.data.totalDebit,
            totalCredit: response.data.totalCredit,
          });
          if (response.data.success) {
            showToast({
              title: dryRun
                ? gt('Validation Passed', {
                    context: 'GL journal test toast',
                  })
                : gt('Import Successful', { context: 'GL journal test toast' }),
              message: response.data.message,
              color: 'success',
              icon: 'check',
            });
          }
        },
        onError: (error) => {
          setResult({
            success: false,
            message:
              error.message ||
              gt('An error occurred', { context: 'GL journal test error' }),
          });
        },
      },
    );
  };

  // Calculate totals for display
  const totalDebit = lines.reduce(
    (sum, line) => sum + (parseFloat(line.debit) || 0),
    0,
  );
  const totalCredit = lines.reduce(
    (sum, line) => sum + (parseFloat(line.credit) || 0),
    0,
  );
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const yesLabel = gt('Yes', { context: 'yes no select' });
  const noLabel = gt('No', { context: 'yes no select' });
  const numPlaceholder = gt('0.00', { context: 'numeric input placeholder' });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <WarningIcon className="h-5 w-5 text-amber-500" />
          <T context="GL journal import test card title">
            GL Journal Import Test
          </T>
        </CardTitle>
        <CardDescription>
          <T context="GL journal import test card description">
            Test FAPI GL Journal imports. Use dry run mode to validate without
            sending to Famous.
          </T>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Warning */}
        <AlertContainer variant="destructive">
          <WarningIcon className="h-4 w-4" />
          <AlertDescription>
            <T context="GL journal admin tool warning">
              <strong>Admin Tool:</strong> Non-dry-run submissions will create
              actual journal entries in Famous. Use with caution.
            </T>
          </AlertDescription>
        </AlertContainer>

        {/* Header Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm">
            <T context="GL journal form section">Journal Header</T>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="journalGroup">
                <T context="GL journal field label">Journal Group *</T>
              </Label>
              <Input
                id="journalGroup"
                placeholder={gt('Enter journal group', {
                  context: 'GL journal placeholder',
                })}
                value={header.journalGroup}
                onChange={(e) =>
                  handleHeaderChange('journalGroup', e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">
                <T context="GL journal field label">Description</T>
              </Label>
              <Input
                id="description"
                placeholder={gt('Max 40 characters', {
                  context: 'GL journal placeholder',
                })}
                maxLength={40}
                value={header.description}
                onChange={(e) =>
                  handleHeaderChange('description', e.target.value)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accessGroup">
                <T context="GL journal field label">Access Group</T>
              </Label>
              <Input
                id="accessGroup"
                placeholder={gt('Optional', {
                  context: 'GL journal optional field placeholder',
                })}
                value={header.accessGroup}
                onChange={(e) =>
                  handleHeaderChange('accessGroup', e.target.value)
                }
              />
            </div>
            <div className="flex gap-4">
              <div className="space-y-2">
                <Label>
                  <T context="GL journal field label">Accrual Flag</T>
                </Label>
                <Select
                  value={header.accrualFlag}
                  onValueChange={(value: 'Y' | 'N') =>
                    handleHeaderChange('accrualFlag', value)
                  }
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Y">{yesLabel}</SelectItem>
                    <SelectItem value="N">{noLabel}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>
                  <T context="GL journal field label">Cash Flag</T>
                </Label>
                <Select
                  value={header.cashFlag}
                  onValueChange={(value: 'Y' | 'N') =>
                    handleHeaderChange('cashFlag', value)
                  }
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Y">{yesLabel}</SelectItem>
                    <SelectItem value="N">{noLabel}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Lines Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">
              <T context="GL journal form section">Journal Lines</T>
            </h4>
            <Button variant="outline" size="sm" onClick={addLine}>
              <PlusIcon className="h-4 w-4 mr-1" />
              <T context="GL journal add line button">Add Line</T>
            </Button>
          </div>

          {lines.map((line, index) => (
            <div
              key={line.id}
              className="border rounded-lg p-4 space-y-3 bg-gray-50/50"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">
                  <T>
                    Line <Var>{index + 1}</Var>
                  </T>
                </span>
                {lines.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(line.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Cost Center</T>
                  </Label>
                  <Input
                    placeholder={gt('12 chars max', {
                      context: 'GL journal line placeholder',
                    })}
                    maxLength={12}
                    value={line.costCenterId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'costCenterId', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Phase</T>
                  </Label>
                  <Input
                    placeholder={gt('6 chars max', {
                      context: 'GL journal line placeholder',
                    })}
                    maxLength={6}
                    value={line.phaseId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'phaseId', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Department</T>
                  </Label>
                  <Input
                    placeholder={gt('6 chars max', {
                      context: 'GL journal line placeholder',
                    })}
                    maxLength={6}
                    value={line.departmentId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'departmentId', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Account</T>
                  </Label>
                  <Input
                    placeholder={gt('12 chars max', {
                      context: 'GL journal line placeholder',
                    })}
                    maxLength={12}
                    value={line.accountId}
                    onChange={(e) =>
                      handleLineChange(line.id, 'accountId', e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Posting Date *</T>
                  </Label>
                  <Input
                    type="date"
                    value={line.postingDate}
                    onChange={(e) =>
                      handleLineChange(line.id, 'postingDate', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Description</T>
                  </Label>
                  <Input
                    placeholder={gt('40 chars max', {
                      context: 'GL journal line placeholder',
                    })}
                    maxLength={40}
                    value={line.description}
                    onChange={(e) =>
                      handleLineChange(line.id, 'description', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Hours</T>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={numPlaceholder}
                    value={line.hours}
                    onChange={(e) =>
                      handleLineChange(line.id, 'hours', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Debit *</T>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={numPlaceholder}
                    value={line.debit}
                    onChange={(e) =>
                      handleLineChange(line.id, 'debit', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <T context="GL journal line field">Credit *</T>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={numPlaceholder}
                    value={line.credit}
                    onChange={(e) =>
                      handleLineChange(line.id, 'credit', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Totals */}
          <div className="flex items-center justify-end gap-6 text-sm">
            <span>
              <T>
                Total Debit:{' '}
                <strong>
                  $<Var>{totalDebit.toFixed(2)}</Var>
                </strong>
              </T>
            </span>
            <span>
              <T>
                Total Credit:{' '}
                <strong>
                  $<Var>{totalCredit.toFixed(2)}</Var>
                </strong>
              </T>
            </span>
            <span className={isBalanced ? 'text-green-600' : 'text-red-600'}>
              {isBalanced ? (
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="h-4 w-4" />{' '}
                  <T context="GL journal balanced">Balanced</T>
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <XCircleIcon className="h-4 w-4" />{' '}
                  <T context="GL journal not balanced">Not Balanced</T>
                </span>
              )}
            </span>
          </div>
        </div>

        <Separator />

        {/* Submit Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch id="dryRun" checked={dryRun} onCheckedChange={setDryRun} />
            <Label htmlFor="dryRun" className="text-sm">
              <T context="GL journal dry run toggle">Dry Run (validate only)</T>
            </Label>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !header.journalGroup || !isBalanced}
            variant={dryRun ? 'default' : 'destructive'}
          >
            {isPending ? (
              <>
                <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                <T context="GL journal processing">Processing...</T>
              </>
            ) : dryRun ? (
              <T context="GL journal validate button">Validate Journal</T>
            ) : (
              <T context="GL journal submit button">Submit to Famous</T>
            )}
          </Button>
        </div>

        {/* Result Section */}
        {result && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="font-medium text-sm">
                <T context="GL journal result section">Result</T>
              </h4>
              <AlertContainer
                variant={result.success ? 'default' : 'destructive'}
              >
                {result.success ? (
                  <CheckCircleIcon className="h-4 w-4" />
                ) : (
                  <XCircleIcon className="h-4 w-4" />
                )}
                <AlertDescription>{result.message}</AlertDescription>
              </AlertContainer>

              {result.totalDebit && result.totalCredit && (
                <p className="text-sm text-gray-600">
                  <T>
                    Total Debit: $<Var>{result.totalDebit}</Var> | Total Credit:
                    $<Var>{result.totalCredit}</Var>
                  </T>
                </p>
              )}

              {result.errorFileContent && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">
                    <T context="GL journal error details heading">
                      Error Details from Famous:
                    </T>
                  </p>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                    {result.errorFileContent}
                  </pre>
                </div>
              )}

              {result.payload && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    <T context="GL journal payload heading">
                      Generated Payload:
                    </T>
                  </p>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                    {result.payload}
                  </pre>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
